import React, { useEffect, useState } from 'react'
import { Card, Button, Descriptions, Table, Tag, Space, Tooltip, Skeleton, Empty, message } from 'antd'
import {
  ArrowLeftOutlined, EditOutlined, PrinterOutlined, FilePdfOutlined,
  MailOutlined, CheckCircleOutlined, FallOutlined, FileDoneOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { paymentStore, PaymentStatus } from './paymentStore'
import { printReceipt, downloadReceiptAsPDF, emailReceipt, receiptBreakdown } from '@/utils/receiptGenerator'
import { useModulePermission } from '@/hooks/useModulePermission'
import { ApiError } from '@/services/api'

const fmt = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const statusCfg: Record<string, { label: string; color: string; bg: string }> = {
  confirmed: { label: 'Confirmed', color: '#065F46', bg: '#D1FAE5' },
  pending:   { label: 'Pending',   color: '#92400E', bg: '#FEF3C7' },
  bounced:   { label: 'Bounced',   color: '#991B1B', bg: '#FEE2E2' },
}

const paymentStatusCfg: Record<PaymentStatus, { label: string; color: string; bg: string }> = {
  unpaid:         { label: 'Unpaid',         color: '#6B7280', bg: '#F3F4F6' },
  partially_paid: { label: 'Partially Paid', color: '#92400E', bg: '#FEF3C7' },
  paid:           { label: 'Paid',           color: '#065F46', bg: '#D1FAE5' },
  tds_deducted:   { label: 'TDS Deducted',   color: '#5B21B6', bg: '#EDE9FE' },
  overdue:        { label: 'Overdue',        color: '#991B1B', bg: '#FEE2E2' },
}

const PaymentDetail: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { canEmail, canDownload, canEdit } = useModulePermission('Finance')
  const [, setTick] = useState(0)
  const [emailingReceipt, setEmailingReceipt] = useState(false)
  const [downloadingReceipt, setDownloadingReceipt] = useState(false)

  useEffect(() => {
    if (!paymentStore.loaded) paymentStore.refresh().then(() => setTick(t => t + 1))
  }, [])

  const rec = id ? paymentStore.get(id) : undefined

  if (!rec) {
    if (!paymentStore.loaded) return <Skeleton active paragraph={{ rows: 8 }} style={{ padding: 24 }} />
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Empty description="Payment not found" />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/payments')}>
          Back to Payments
        </Button>
      </div>
    )
  }

  const { gross, base, gst, tds, received } = receiptBreakdown(rec)
  const sCfg = statusCfg[rec.status] || statusCfg.pending
  const pCfg = paymentStatusCfg[rec.paymentStatus ?? 'unpaid']

  const invoiceColumns = [
    { title: 'Invoice #', dataIndex: 'invoiceNumber', render: (v: string, r: any) => (
      <span
        style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#1677ff', cursor: 'pointer' }}
        onClick={() => navigate(`/invoices/${r.invoiceId}`)}
      >
        {v}
      </span>
    ) },
    { title: 'Applied Amount', dataIndex: 'applied', align: 'right' as const, render: (v: number) => (
      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{fmt(v)}</span>
    ) },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/payments')} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 className="page-title">{rec.receiptNumber}</h2>
            <span className="pill" style={{ background: sCfg.bg, color: sCfg.color }}>{sCfg.label}</span>
            <span className="pill" style={{ background: pCfg.bg, color: pCfg.color }}>{pCfg.label}</span>
          </div>
          <p className="page-subtitle">{rec.customer} · {rec.date}</p>
        </div>
        <Space wrap>
          {canEdit && (
            <Button icon={<EditOutlined />} onClick={() => navigate(`/payments/${rec.id}/edit`)}>
              Edit
            </Button>
          )}
          {canEmail && (
            <Tooltip title="Email Receipt">
              <Button
                icon={<MailOutlined />}
                loading={emailingReceipt}
                onClick={async () => {
                  setEmailingReceipt(true)
                  try {
                    const sentTo = await emailReceipt(rec)
                    message.success(`Receipt emailed to ${sentTo}`)
                  } catch (err) {
                    message.error(err instanceof ApiError ? err.message : 'Could not send the email. Please try again.')
                  } finally {
                    setEmailingReceipt(false)
                  }
                }}
              />
            </Tooltip>
          )}
          {canDownload && (
            <Tooltip title="Download Receipt PDF">
              <Button
                icon={<FilePdfOutlined />}
                loading={downloadingReceipt}
                onClick={async () => {
                  setDownloadingReceipt(true)
                  try {
                    await downloadReceiptAsPDF(rec)
                    message.success('Receipt PDF downloaded successfully!')
                  } catch (err) {
                    message.error(err instanceof ApiError ? err.message : 'Failed to generate PDF download. Please try again.')
                  } finally {
                    setDownloadingReceipt(false)
                  }
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Print Receipt">
            <Button icon={<PrinterOutlined />} onClick={() => printReceipt(rec)} />
          </Tooltip>
        </Space>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {/* Receipt info */}
        <Card title="Receipt Info" style={{ flex: 1, minWidth: 300 }}>
          <Descriptions column={1} size="small" styles={{ label: { width: 150 } }}>
            <Descriptions.Item label="Receipt Number">
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{rec.receiptNumber}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Customer">{rec.customer} ({rec.customerId})</Descriptions.Item>
            <Descriptions.Item label="Payment Date">
              <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{rec.date}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Mode"><Tag>{rec.mode}</Tag></Descriptions.Item>
            {rec.reference && (
              <Descriptions.Item label="Reference / UTR #">
                <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{rec.reference}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Amount breakdown — same Gross → TDS → Net layout as the Record
            Payment form's Receipt Summary, so the two stay visually consistent. */}
        <Card title="Amount Breakdown" style={{ flex: 1, minWidth: 300 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 2px' }}>
            <span style={{ fontSize: 13, color: '#4A5568' }}>Base Amount</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 14, fontWeight: 600, color: '#0B1426' }}>{fmt(base)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 2px' }}>
            <span style={{ fontSize: 13, color: '#4A5568' }}>GST Amount</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 14, fontWeight: 600, color: '#0B1426' }}>{fmt(gst)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 2px', marginTop: 4, borderTop: '1px dashed #CBD5E1' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0B1426' }}>Gross Amount</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 15, fontWeight: 800, color: '#0B1426' }}>{fmt(gross)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', marginTop: 8, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>
              <FallOutlined style={{ marginRight: 6, fontSize: 11 }} />
              TDS Deducted
            </span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 14, fontWeight: 700, color: '#92400E' }}>
              {tds > 0 ? `− ${fmt(tds)}` : fmt(tds)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '14px 16px', marginTop: 14, background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, color: '#065F46' }}>
              <CheckCircleOutlined />
              Total Amount Received / Credited
            </span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 22, fontWeight: 800, color: '#059669' }}>{fmt(received)}</span>
          </div>
        </Card>
      </div>

      {/* Invoices applied */}
      <Card title="Invoices Settled" style={{ marginTop: 20 }}>
        <Table
          dataSource={rec.invoices}
          columns={invoiceColumns}
          rowKey="invoiceId"
          pagination={false}
          size="small"
        />
      </Card>

      {(rec.tdsCertRef || rec.tdsCertUrl || rec.form16a || rec.form16aAttached || rec.gstCertRef || rec.gstCertUrl) && (
        <Card title="TDS & GST Certificates" style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                TDS Certificate
              </div>
              <Descriptions column={1} size="small" styles={{ label: { width: 140 } }}>
                {rec.tdsCertRef && <Descriptions.Item label="Certificate Ref"><span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{rec.tdsCertRef}</span></Descriptions.Item>}
                {rec.form16a && <Descriptions.Item label="Form 16A Ref"><span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{rec.form16a}</span></Descriptions.Item>}
                <Descriptions.Item label="Form 16A Attached">
                  <Tag color={rec.form16aAttached ? 'success' : 'default'}>{rec.form16aAttached ? 'Yes' : 'No'}</Tag>
                </Descriptions.Item>
                {rec.tdsCertUrl && (
                  <Descriptions.Item label="File">
                    <a href={rec.tdsCertUrl} target="_blank" rel="noreferrer" style={{ color: '#059669' }}>
                      <FileDoneOutlined /> View file
                    </a>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#1E40AF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                GST Certificate
              </div>
              <Descriptions column={1} size="small" styles={{ label: { width: 140 } }}>
                {rec.gstCertRef && <Descriptions.Item label="Certificate Ref"><span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{rec.gstCertRef}</span></Descriptions.Item>}
                {rec.gstCertUrl && (
                  <Descriptions.Item label="File">
                    <a href={rec.gstCertUrl} target="_blank" rel="noreferrer" style={{ color: '#059669' }}>
                      <FileDoneOutlined /> View file
                    </a>
                  </Descriptions.Item>
                )}
                {!rec.gstCertRef && !rec.gstCertUrl && <Descriptions.Item label=" ">—</Descriptions.Item>}
              </Descriptions>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}

export default PaymentDetail
